import { Locale, setDefaultOptions } from 'date-fns'
import i18n, { BackendModule, InitOptions, ReadCallback, ResourceKey } from 'i18next'
import moment from 'moment'
import { initReactI18next } from 'react-i18next'

import { Locales } from '@/types/locale'

import { getCurrentLocale } from './lib/i18n/locale-utils'

type LocaleModule = {
	default: ResourceKey
	momentLang: string
	momentLocale?: moment.LocaleSpecification
	dateLocale?: Locale
}

const localeModules = new Map(
	Object.entries(import.meta.glob('./locales/*/index.ts')).map(([url, fn]) => [url.split('/').slice(-2, -1)[0], fn]),
)

const loadedLocaleModules = new Map<string, LocaleModule>()

const languagesBackendModule: BackendModule = {
	type: 'backend',
	init: function () {
		return undefined
	},
	read: function (language: string, _namespace: string, callback: ReadCallback): void {
		if (localeModules.has(language)) {
			localeModules.get(language)!()
				.then((module: LocaleModule) => {
					loadedLocaleModules.set(language, module)

					callback(null, module.default)
				})
				.catch((e) => callback(e, null))
		} else {
			callback(new Error(`Unknown language: ${language}`), null)
		}
	},
}

export function initializeI18n(options?: InitOptions) {
	const language = getCurrentLocale()

	return i18n
		.use(languagesBackendModule)
		.use(initReactI18next)
		.init(
			{
				fallbackLng: Locales.enUS,
				interpolation: {
					escapeValue: false,
				},
				keySeparator: false,
				nsSeparator: false,
				lng: language,
				returnNull: false,
				load: 'currentOnly',
				...(options ?? {}),
			},
			() => {
				const module = loadedLocaleModules.get(i18n.language)
				if (module) {
					// `._config` is because the exported value is a processed locale, but we need the spec to call `moment.locale()`.
					// Moment relies on side-effect, which do not work in a dynamic import.
					moment.locale(module.momentLang, module.momentLocale?._config)

					setDefaultOptions({ locale: module.dateLocale })
				} else {
					moment.locale('en')

					setDefaultOptions({ locale: undefined })
				}

				document.documentElement.lang = i18n.language
			},
		)
}
